import {
  recordUpgradeMessageDismissed,
  recordUpgradePursued,
} from '@/Analytics/SubscriptionUpgrade';
import useSimpleModal from '@/Components/Modal/useSimpleModal';
import usePujFlag from '@/Hooks/usePujFlag';
import {
  createSubscriptionsModalContent,
  getTitleFromSubscription,
  SUBSCRIPTIONS_MODAL_TITLE,
  UPGRADE_TEXT,
  useChannelSubscriptions,
} from '@/Hooks/useSubscription';
import { selectors } from '@/Store';
import navigateToPackage, { getOfferIdFromLinearViewingMethod } from '@/Utils/Puj';
import { getVideoImg } from '@/Utils/VideoImage';
import { ImageAssetType } from '@skytvnz/sky-app-store/lib/types/enums/ImageAssetType';
import { LinearChannel } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { isEmpty } from 'ramda';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styles from './styles.module.scss';

interface ChannelColumnProps {
  channels: Omit<LinearChannel, 'slots'>[];
  onChannelIconClick?: (channel: Omit<LinearChannel, 'slots'>) => void;
}

const ChannelColumn: FC<ChannelColumnProps> = ({ channels }) => {
  const { openModal } = useSimpleModal();
  const location = useLocation();
  const userSubscriptions = useSelector(selectors.subscription.subscriptions);
  const requiredSubscriptions = useChannelSubscriptions();
  const subscriptionTitles = getTitleFromSubscription(userSubscriptions);
  const history = useHistory();
  const { isPUJEnabled } = usePujFlag();
  const handleChannelClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, channel: Omit<LinearChannel, 'slots'>) => {
      e.preventDefault(); // Prevent navigation for conditional logic
      const offerId = getOfferIdFromLinearViewingMethod(channel);
      const subscriptions = requiredSubscriptions(channel?.id) ?? [];
      const missingSubscriptions = subscriptions.filter(
        sub => !userSubscriptions.some(userSub => userSub.id === sub.id),
      );

      const requiredSubscriptionTitles = getTitleFromSubscription(subscriptions);

      if (!isEmpty(missingSubscriptions) && isPUJEnabled) {
        openModal({
          title: SUBSCRIPTIONS_MODAL_TITLE,
          content: createSubscriptionsModalContent(missingSubscriptions),
          okText: UPGRADE_TEXT,
          cancelable: true,
          onOk: () => {
            recordUpgradePursued(subscriptionTitles, requiredSubscriptionTitles);
            navigateToPackage({
              offerId,
              history,
              location,
              openModal,
            });
          },
          onCancel: () => {
            recordUpgradeMessageDismissed(subscriptionTitles, requiredSubscriptionTitles);
          },
          onClose: () => {
            recordUpgradeMessageDismissed(subscriptionTitles, requiredSubscriptionTitles);
          },
        });
      } else {
        // Navigate if the content is already subscribed
        history.push(`/watch-tv/${channel.id}?playerReferrer=TV Guide`);
      }
    },
    [
      history,
      isPUJEnabled,
      openModal,
      requiredSubscriptions,
      location,
      subscriptionTitles,
      userSubscriptions,
    ],
  );

  return (
    <div className={styles.channels}>
      <div className={styles.title}>
        <span className={styles.text}>Channels</span>
      </div>
      {channels.map(channel => {
        const { id, title, tileImage, number } = channel;
        return (
          <Link
            to={`/watch-tv/${id}?playerReferrer=TV Guide`}
            className={styles.channel}
            key={id}
            onClick={e => handleChannelClick(e, channel)}
          >
            <span className={styles.channelImage}>
              {tileImage?.uri && (
                <img
                  alt={title}
                  src={getVideoImg(ImageAssetType.ContentTileVertical, tileImage.uri)}
                />
              )}
            </span>
            <span className={styles.channelNumber}>{number.toString().padStart(3, '0')}</span>
          </Link>
        );
      })}
    </div>
  );
};

export default ChannelColumn;
